import React from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
  SvgIcon,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ReactComponent as Vector } from "./ImageforDashboardStudent/Vector.svg";
import { ReactComponent as info } from "./ImageforDashboardStudent/info-circle.svg";
import { ReactComponent as lock } from "./ImageforDashboardStudent/lock-unlocked.svg";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import {
  clearListSubscription,
  listSubscription,
} from "../../Redux/Slices/Subscription/ListSubscriptionSlice";
import { AddSubscription } from "../../Redux/Slices/Subscription/AddSubscriptionSlice";
import { ReactComponent as Settingpage } from "../../Assets/Images/Setting.svg";
import { ReactComponent as Line } from "./ImageforDashboardStudent/bar-chart-02.svg";
import { ReactComponent as Cardbook } from "./ImageforDashboardStudent/file-question-03.svg";

const ProgressCard = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: listQuestionbank } = useAppSelector(
    (state) => state.ListDashboardprogressSlice
  );
  const datalist: any = listQuestionbank?.data;

  const percentage =
    datalist?.total_attempted_questions === 0
      ? 0
      : (
          (datalist?.total_attempted_questions / datalist?.total_questions) *
          100
        ).toFixed(1); // e.g., 13.7%

  const correctPercent =
    (datalist?.total_correct_questions / datalist?.total_questions) * 100 || 0;
  const wrongPercent =
    (datalist?.total_wrong_questions / datalist?.total_questions) * 100 ||0;
  const remainingPercent =
    datalist?.total_questions - correctPercent - wrongPercent;
  const progressBackground = `linear-gradient(to right,
  #00c853 0%,
  #00c853 ${correctPercent}%,
  #d50000 ${correctPercent}%,
  #d50000 ${correctPercent + wrongPercent}%,
  #2979ff ${correctPercent + wrongPercent}%,
  #2979ff 100%
)`;

  // pricing popup
  const { data: listUserPackageSlice } = useAppSelector(
    (state) => state.ListUserPackageSlice
  );
  const Packages: any = Array.isArray(listUserPackageSlice?.data)
    ? listUserPackageSlice?.data.map((user, index) => ({
        ...user,
        index: index + 1,
      }))
    : [];
  const { data: DashboardCards } = useAppSelector(
    (state) => state.DashboardChart
  );

  const Dashboarduser: any = DashboardCards?.data;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: "#fff",
        width: "100%",
        mx: "auto",
        boxShadow: "0px 2px 12px rgba(0,0,0,0.05)",
      }}
    >
      {/* Row: Questions Attempted + Progress */}
      <Grid container spacing={2} alignItems="flex-start" mb={2}>
        {/* Left Side: Questions Attempted */}
        <Grid item xs={12} md={8}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignContent: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: "18px" }}
              mb={1}
            >
              Your Progress
            </Typography>
            <Chip
              label={
                Dashboarduser?.is_subscribed === true ? "Premium" : "Free Tier"
              }
              size="small"
              sx={{
                backgroundColor: "#009DFF1A",
                color: "#009DFF",
                fontWeight: 600,
              }}
            />
          </div>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={3}>
              <Typography
                variant="body2"
                sx={{ color: "#6b7280", fontSize: "15px" }}
              >
                Questions Attempted
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {datalist?.total_attempted_questions || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#00c853",
                  }}
                />
                Correct
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="#00c853">
                {datalist?.total_correct_questions|| 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={2.5}>
              <Typography
                variant="body2"
                color="error"
                sx={{
                  color: "#ef4444",
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#ef4444",
                  }}
                />
                Incorrect
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="#ef4444">
                {datalist?.total_wrong_questions || 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  color: "#9ca3af",
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#9ca3af",
                  }}
                />{" "}
                Unattempted
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="#9ca3af">
                {datalist?.total_unattempted_questions || 0}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Side: Progress */}
        <Grid item xs={12} md={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "18px", fontWeight: 500 }}
            >
              Progress{" "}
              <SvgIcon
                component={info}
                sx={{ fill: "white", width: "13px", height: "13px" }}
              />
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "15px", fontWeight: 500 }}
            >
              {percentage}%
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundImage: progressBackground,
              backgroundColor: "transparent", // override MUI default
              "& .MuiLinearProgress-bar": {
                display: "none", // hide actual progress bar
              },
            }}
          />
          <Box
            mb={1}
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              margin: "15px 0px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              color="#6b7280"
              sx={{ fontSize: "15px" }}
            >
              {Dashboarduser?.is_subscribed === true ? "Premium" : "Free"}{" "}
              access: {datalist?.total_questions} questions
            </Typography>
            {Dashboarduser?.is_subscribed === false && (
              <span
                style={{
                  color: "#4361EE",
                  fontWeight: 600,
                  fontSize: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <SvgIcon
                  component={lock}
                  sx={{ fill: "white", fontSize: "18px" }}
                />
                {datalist?.total_questions_paid_version}+ locked
              </span>
            )}
          </Box>

          {/* Button */}
          {Dashboarduser?.is_subscribed === false && (
            <>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#00aaff",
                  textTransform: "none",
                  borderRadius: "5px",
                  height: "30px",
                  color: "white",
                  fontSize: "14px",
                  boxShadow: "0px 0px 10px #2196f3", // Stronger and more visible
                  "&:hover": {
                    backgroundColor: "#009dff",
                    boxShadow: "0px 0px 10px #2196f3", // Stronger shadow on hover
                    "& .MuiSvgIcon-root": {
                      fill: "#ffffff", // This changes the SVG icon color on hover
                    },
                  },
                }}
                onClick={() => {
                  dispatch(clearListSubscription());
                  dispatch(AddSubscription());
                  dispatch(
                    listSubscription({
                      package_id: Packages[0]?.package_id,
                    })
                  );
                }}
              >
                <SvgIcon
                  component={Vector}
                  sx={{
                    fill: "#00aaff",
                    marginRight: "12px",
                    transition: "fill 0.2s",
                  }}
                />
                Unlock {datalist?.total_questions_paid_version}+ Questions
              </Button>
              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                mt={1}
                sx={{
                  fontSize: "13px",
                  backgroundColor: "#F9FAFB",
                  borderRadius: "5px",
                  margin: {
                    xs: "0px", // for mobile and small screens
                    sm: "0px", // optional, but you can keep it explicit
                    md: "0px", // for medium and up
                    lg: "2px 75px",
                  },
                  padding: "4px 8px",
                }}
                color="text.secondary"
              >
                Most students upgrade within a week
              </Typography>

              <Typography
                variant="caption"
                display="block"
                textAlign="center"
                mt={1}
                sx={{
                  fontSize: "13px",
                  borderRadius: "5px",
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                color="text.secondary"
              >
                <SvgIcon
                  component={Cardbook}
                  inheritViewBox
                  sx={{ fill: "white", marginRight: "3px" }}
                />{" "}
                <span style={{ marginRight: "5px", marginLeft: "5px" }}>
                  Full-length mocks
                </span>{" "}
                •{" "}
                <AccessTimeIcon
                  fontSize="small"
                  sx={{ opacity: 0.6, marginRight: "5px", marginLeft: "5px" }}
                />{" "}
                <span style={{ marginRight: "5px", marginLeft: "5px" }}>
                  Timed exams
                </span>{" "}
                •{" "}
                <SvgIcon
                  component={Line}
                  inheritViewBox
                  sx={{ marginRight: "5px", marginLeft: "5px" }}
                />{" "}
                {datalist?.total_questions_paid_version}+ Qs
              </Typography>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProgressCard;
